import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import s from './styles.module.scss'
import { dialCodes } from '../../../data/dialCodes'

interface DialCodeSelectProps {
    value: string
    onChange: (iso2: string) => void
    error?: boolean
}

const MENU_MAX_HEIGHT = 320
const MENU_WIDTH = 320
const GAP = 4
const EDGE = 8

export const DialCodeSelect = (props: DialCodeSelectProps) => {
    const { value, onChange, error = false } = props
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [activeIndex, setActiveIndex] = useState(0)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const searchRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLUListElement>(null)

    const selected = dialCodes.find((d) => d.iso2 === value)

    const options = useMemo(() => {
        const q = query.trim().toLowerCase().replace(/^\+/, '')
        if (!q) return dialCodes
        return dialCodes.filter(
            (d) => d.name.toLowerCase().includes(q) || d.iso2.toLowerCase() === q || d.code.startsWith(q)
        )
    }, [query])

    // Open below the trigger unless there isn't room and there's more space above
    const position = () => {
        if (!triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight
        const viewportWidth = window.innerWidth
        const spaceBelow = viewportHeight - rect.bottom - GAP - EDGE
        const spaceAbove = rect.top - GAP - EDGE
        const openUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow
        const width = Math.min(MENU_WIDTH, viewportWidth - EDGE * 2)
        const left = Math.max(EDGE, Math.min(rect.left, viewportWidth - width - EDGE))

        setMenuStyle({
            left,
            width,
            maxHeight: Math.min(MENU_MAX_HEIGHT, openUp ? spaceAbove : spaceBelow),
            ...(openUp ? { bottom: window.innerHeight - rect.top + GAP } : { top: rect.bottom + GAP }),
        })
    }

    useLayoutEffect(() => {
        if (open) position()
    }, [open])

    useEffect(() => {
        if (!open) return
        searchRef.current?.focus({ preventScroll: true })

        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return
            close()
        }
        window.addEventListener('scroll', position, true)
        window.addEventListener('resize', position)
        window.visualViewport?.addEventListener('resize', position)
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('touchstart', onPointerDown)
        return () => {
            window.removeEventListener('scroll', position, true)
            window.removeEventListener('resize', position)
            window.visualViewport?.removeEventListener('resize', position)
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('touchstart', onPointerDown)
        }
    }, [open])

    useEffect(() => {
        setActiveIndex(0)
    }, [query])

    useEffect(() => {
        const item = listRef.current?.children[activeIndex] as HTMLElement | undefined
        item?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex])

    const openMenu = () => {
        setQuery('')
        setActiveIndex(Math.max(0, dialCodes.findIndex((d) => d.iso2 === value)))
        setOpen(true)
    }

    const close = (refocus = false) => {
        setOpen(false)
        if (refocus) triggerRef.current?.focus()
    }

    const choose = (iso2: string) => {
        onChange(iso2)
        close(true)
    }

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveIndex((i) => Math.min(i + 1, options.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIndex((i) => Math.max(i - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (options[activeIndex]) choose(options[activeIndex].iso2)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            close(true)
        } else if (e.key === 'Tab') {
            close()
        }
    }

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`${s.trigger} ${open ? s.open : ''} ${error ? s.error : ''}`}
                onClick={() => (open ? close() : openMenu())}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Country dialling code: ${selected?.name} +${selected?.code}`}
            >
                <span>+{selected?.code}</span>
                <span className={s.chevron} aria-hidden="true" />
            </button>
            {open &&
                createPortal(
                    <div ref={menuRef} className={s.menu} style={menuStyle}>
                        <div className={s.searchWrapper}>
                            <input
                                ref={searchRef}
                                className={s.search}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.currentTarget.value)}
                                onKeyDown={onSearchKeyDown}
                                placeholder="Search country or code"
                                aria-label="Search countries"
                                autoComplete="off"
                            />
                        </div>
                        {options.length ? (
                            <ul ref={listRef} className={s.list} role="listbox" aria-label="Country dialling codes">
                                {options.map((d, index) => (
                                    <li
                                        key={d.iso2}
                                        role="option"
                                        aria-selected={d.iso2 === value}
                                        className={`${s.option} ${d.iso2 === value ? s.selected : ''} ${index === activeIndex ? s.active : ''}`}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => choose(d.iso2)}
                                    >
                                        <span className={s.name}>{d.name}</span>
                                        <span className={s.code}>+{d.code}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className={s.empty}>No countries found</p>
                        )}
                    </div>,
                    document.body
                )}
        </>
    )
}
